import React, { useMemo, useState } from 'react';
import LazyLoad from 'react-lazyload';

import { Service } from 'container/Home/mainMenuList';
import NewsList, { NewsListBlocks } from 'container/Home/newsList';
import { QuickNavigationData } from 'container/Home/quickNavigation';
import ReportList, { AnalysisReport } from 'container/Home/reportList';
import VideoList, { VideoListBlocks } from 'container/Home/videoList';
import HelmetComponent from 'container/helmet';
import { getBlockData } from 'helpers/pageData';
import { getOgDataPage } from 'utils/menu';

const Banner = React.lazy(() => import('container/Home/bannerHome'));
const QuickNavigation = React.lazy(() => import('container/Home/quickNavigation'));
const MainMenuList = React.lazy(() => import('container/Home/mainMenuList'));

export type HomeData =
  | QuickNavigationData
  | Service
  | AnalysisReport
  | NewsListBlocks
  | VideoListBlocks;

const Home: React.FC<BasePageDataTypes<HomeData>> = ({
  blocks,
  banners,
  seoData,
  pageData,
}) => {
  const [newsSelected, setNewsSelected] = useState('news');

  const customerServiceBlock = useMemo(
    () => getBlockData('customer_service', blocks) as Service,
    [blocks],
  );
  const utilitiesBlock = useMemo(
    () => getBlockData('utilities', blocks) as QuickNavigationData,
    [blocks],
  );
  const reportBlocks = useMemo(
    () => getBlockData('analysis_report', blocks) as AnalysisReport,
    [blocks],
  );
  const newsListBlocks = useMemo(
    () => getBlockData('news', blocks) as NewsListBlocks,
    [blocks],
  );
  const videoListBlocks = useMemo(
    () => getBlockData('video', blocks) as VideoListBlocks,
    [blocks],
  );

  const newsDisclosureBlocks = useMemo(
    () => getBlockData('news_disclosure', blocks) as NewsListBlocks,
    [blocks],
  );
  const newsSgdckBlocks = useMemo(
    () => getBlockData('news_sgdck', blocks) as NewsListBlocks,
    [blocks],
  );

  const radioGroup = useMemo(() => {
    const newsObj = newsListBlocks && {
      value: 'news',
      title: newsListBlocks.title,
    };
    const disclosureObj = newsDisclosureBlocks && {
      value: 'news_disclosure',
      title: newsDisclosureBlocks.title,
    };
    const sgdckBlocks = newsSgdckBlocks && {
      value: 'news_sgdck',
      title: newsSgdckBlocks.title,
    };
    return [
      newsObj,
      disclosureObj,
      sgdckBlocks,
    ].filter((item) => item);
  }, [
    newsListBlocks,
    newsDisclosureBlocks,
    newsSgdckBlocks,
  ]);

  const newsBlocks = useMemo(() => {
    switch (newsSelected) {
      case 'news':
        return newsListBlocks;
      case 'news_disclosure':
        return newsDisclosureBlocks;
      default:
        return newsSgdckBlocks;
    }
  }, [
    newsListBlocks,
    newsDisclosureBlocks,
    newsSgdckBlocks,
    newsSelected,
  ]);

  return (
    <>
      <HelmetComponent seoData={seoData} ogData={getOgDataPage(pageData)} />
      <Banner banners={banners} />
      <QuickNavigation blocks={utilitiesBlock} />
      <MainMenuList blocks={customerServiceBlock} />
      <LazyLoad offset={100} once>
        <ReportList blocks={reportBlocks} />
        <NewsList
          blocks={newsBlocks}
          selectedType={newsSelected}
          handleSetSelectedType={(type: string) => setNewsSelected(type)}
          radioList={radioGroup}
        />
        <VideoList blocks={videoListBlocks} />
      </LazyLoad>
    </>
  );
};
export default Home;
